// Vault-Sync Summary fuer /daten/vault. Enthaelt registrierte Macs,
// Activity-Feed, Storage-Stats. Polling 30 s und WS-Abo "vault", beides nur
// solange die Seite offen ist (beobachteVault, lib/bei-bedarf.ts); start*
// spielt nur den SWR-Cache ein. Nach Mutationen (registerMac/deleteMac/
// Konflikt-Resolve) ruft die Page refreshVaultSummary() für Soforth-Update.

import { untrack } from "svelte";
import { SvelteMap } from "svelte/reactivity";
import { beiBedarf } from "./bei-bedarf";
import { registerRevalidator } from "./revalidate.svelte";

import { loadPersisted, persist } from "./persistent-summary.svelte";
import { subscribe as wsSubscribe } from "./ws-hub.svelte";

export interface VaultMac {
  id: string;
  name: string;
  last_seen: string;
  created: string;
}

export interface VaultEvent {
  id: number;
  ts: string;
  mac_id: string;
  action: string;
  path: string;
  hash: string;
  conflict_with: string;
  size: number;
}

export interface VaultConflict {
  id: number;
  ts: string;
  mac_id: string;
  mac_name: string;
  path: string;
  conflict_path: string;
}

export interface VaultSummary {
  macs: VaultMac[];
  events: VaultEvent[];
  used_bytes: number;
  available_bytes: number;
  file_count: number;
  index_bytes: number;
  conflicts: VaultConflict[];
  /** Zahl der offenen Konflikte -- auch dann, wenn ihre Namen fehlen. */
  conflict_count?: number;
  /** Die Vault-PIN ist eingerichtet und diese Sitzung nicht freigegeben:
   *  der Server laesst dann alle Namen weg (events und conflicts leer). */
  locked?: boolean;
}

const STORAGE_KEY = "vault";

const _state = new SvelteMap<string, VaultSummary>();
let _started = false;
let _refreshPending = false;

async function refresh(): Promise<void> {
  try {
    const r = await fetch("/v2-api/vault/summary", { credentials: "include" });
    if (!r.ok) return;
    const s = (await r.json()) as VaultSummary;
    _state.set("current", s);
    // Im Browser liegt nur die Fassung OHNE Namen: der Zwischenspeicher
    // ueberlebt die Freigabe der PIN und stuende beim naechsten Oeffnen
    // sonst mit allen Dateinamen da, bevor der Server gefragt ist.
    persist(STORAGE_KEY, { ...s, events: [], conflicts: [] });
  } catch {
    // ignore
  }
}

// scheduleRefresh debounced auf 200 ms: kommen mehrere ws-Frames in
// schneller Folge (Schwall-Upload) wird genau ein Refetch ausgefuehrt.
function scheduleRefresh(): void {
  if (_refreshPending) return;
  _refreshPending = true;
  setTimeout(() => {
    _refreshPending = false;
    void refresh();
  }, 200);
}

const _bedarf = beiBedarf({
  hole: refresh,
  takt: 30_000,
  abo: () => wsSubscribe("vault", () => scheduleRefresh()),
});

export function startVaultSummary(): void {
  if (_started) return;
  _started = true;
  registerRevalidator(() => {
    if (_bedarf.aktiv()) void refresh();
  });
  const cached = loadPersisted<VaultSummary>(STORAGE_KEY);
  if (cached) _state.set("current", cached);
}

// Anmelden, solange die Seite offen ist; der Rückgabewert meldet wieder ab.
// In Svelte: $effect(() => { if (zuSehen()) return beobachteVault(); }).
export function beobachteVault(): () => void {
  return untrack(() => {
    startVaultSummary();
    return _bedarf.beobachte();
  });
}

export function stopVaultSummary(): void {
  _started = false;
  _bedarf.beende();
  _state.clear();
}

export function getVaultSummary(): VaultSummary | undefined {
  return _state.get("current");
}

export async function refreshVaultSummary(): Promise<void> {
  await refresh();
}
